import { useEffect, useState } from 'react';
import { IconCheck, IconCopy, IconSend, IconTrash } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { TableRowActions } from 'ui';

import { USER_ROLE_TO_LABEL } from '../../core/constants';
import { EXPIRES_SOON_DAYS, formatExpiry, getDaysRemaining } from './invite-expiry';

const ExpiryCell = ({ expiresAt }) => {
  const days = getDaysRemaining(expiresAt);
  if (days > 0 && days < EXPIRES_SOON_DAYS) {
    return (
      <span className="w-fit rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-caption1 font-semibold uppercase text-orange-700">
        Expires soon ({formatExpiry(days)})
      </span>
    );
  }
  return <span className={days <= 0 ? 'text-gray-500' : undefined}>{formatExpiry(days)}</span>;
};

ExpiryCell.propTypes = { expiresAt: PropTypes.number.isRequired };

const getInviteName = (invite) => [invite.firstName, invite.lastName].filter(Boolean).join(' ');

export const INVITE_ACTIONS = {
  copyLink: 'Copy Link',
  resend: 'Resend',
  revoke: 'Revoke',
};

// Copy Link stays visible as an icon; Resend and Revoke go in the "More actions" menu.
const inviteActions = [INVITE_ACTIONS.copyLink, INVITE_ACTIONS.resend, INVITE_ACTIONS.revoke];

const getActionIcon = (action, copied) => {
  switch (action) {
    case INVITE_ACTIONS.copyLink:
      return copied ? <IconCheck size={20} className="text-green-700" /> : <IconCopy size={20} />;
    case INVITE_ACTIONS.resend:
      return <IconSend size={20} />;
    case INVITE_ACTIONS.revoke:
      return <IconTrash size={20} className="text-red-500" />;
    default:
      return null;
  }
};

const PendingInvitesTable = ({ data, busyInviteId, onResend, onRevoke }) => {
  // Invite whose link was just copied; its copy icon briefly turns into a check mark.
  const [copiedInviteId, setCopiedInviteId] = useState(null);

  useEffect(() => {
    if (!copiedInviteId) return undefined;
    const timer = setTimeout(() => setCopiedInviteId(null), 2000);
    return () => clearTimeout(timer);
  }, [copiedInviteId]);

  const handleAction = async (invite, action) => {
    switch (action) {
      case INVITE_ACTIONS.copyLink:
        await navigator.clipboard.writeText(invite.inviteUrl);
        setCopiedInviteId(invite.inviteId);
        break;
      case INVITE_ACTIONS.resend:
        onResend(invite);
        break;
      case INVITE_ACTIONS.revoke:
        onRevoke(invite);
        break;
      default:
        break;
    }
  };

  if (!data.length) return null;

  return (
    <section className="space-y-3" aria-labelledby="pending-invites-heading">
      <h2
        id="pending-invites-heading"
        className="flex items-center space-x-2 text-lg font-semibold dark:text-white-100"
      >
        <span>Pending invitations</span>
        <span className="rounded-full bg-gray-50 px-2 text-sm font-medium text-gray-800">{data.length}</span>
      </h2>

      <div className="relative overflow-x-auto sm:rounded-lg">
        <table className="data-table w-full text-left text-base text-gray-950 dark:text-gray-400">
          <thead className="bg-white-100 text-sm uppercase text-gray-400 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((invite) => {
              const busy = busyInviteId === invite.inviteId;
              const name = getInviteName(invite);
              return (
                <tr
                  key={invite.inviteId}
                  className="border-b border-gray-50 bg-white-100 hover:bg-gray-10 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-600"
                >
                  <td className="px-4 py-2">{invite.email}</td>
                  <td className="px-4 py-2">{name || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2">{USER_ROLE_TO_LABEL[invite.role]}</td>
                  <td className="px-4 py-2">
                    <ExpiryCell expiresAt={invite.expiresAt} />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end">
                      <TableRowActions
                        actions={inviteActions}
                        disabled={busy}
                        onSelectAction={(action) => handleAction(invite, action)}
                        getActionIcon={(action) => getActionIcon(action, copiedInviteId === invite.inviteId)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

PendingInvitesTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      inviteId: PropTypes.string.isRequired,
      inviteUrl: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      role: PropTypes.string.isRequired,
      expiresAt: PropTypes.number.isRequired,
    }),
  ).isRequired,
  busyInviteId: PropTypes.string,
  onResend: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
};

PendingInvitesTable.defaultProps = {
  busyInviteId: null,
};

export default PendingInvitesTable;
